class InfiniteScrollPagination extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.nextUrl = this.dataset.nextUrl || null;
    this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
    this.grid = document.getElementById('product-grid');
    this.spinner = this.querySelector('.infinite-scroll-pagination__spinner');
    this.loading = false;
  }

  async connectedCallback() {
    if (!('IntersectionObserver' in window)) return;

    // Landed directly on a page other than the first (refresh, back button, shared link) -
    // backfill every earlier page so scrolling up shows the full collection, not just
    // the page the URL happened to point at.
    if (this.grid && this.currentPage > 1) {
      await this.backfillPreviousPages();
    }

    if (!this.nextUrl || !this.grid) return;

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), { rootMargin: '400px' });
    this.observer.observe(this);
  }

  async backfillPreviousPages() {
    this.loading = true;
    this.spinner?.removeAttribute('hidden');

    const fragment = document.createDocumentFragment();
    const firstItem = this.grid.firstElementChild;

    try {
      for (let page = 1; page < this.currentPage; page++) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', page);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Failed to fetch page ${page}: ${response.status}`);

        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');

        doc.querySelectorAll('#product-grid .grid__item').forEach((item) => {
          fragment.appendChild(item);
        });
      }

      this.grid.insertBefore(fragment, firstItem);
    } catch (error) {
      console.error(error);
    } finally {
      // Reveal the grid (hidden via the infinite-scroll-loading class, see
      // main-collection-product-grid.liquid) only now, so page 1..N-1 and the current page's
      // items all appear together - never a partial flash of just the current page while
      // backfilling runs. Reveal on error too, so a failed fetch doesn't leave it hidden.
      this.grid.classList.remove('infinite-scroll-loading');
      this.spinner?.setAttribute('hidden', '');
      this.loading = false;
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  handleIntersect(entries) {
    if (entries[0].isIntersecting && !this.loading && this.nextUrl) {
      this.loadNextPage();
    }
  }

  async loadNextPage() {
    this.loading = true;
    this.spinner?.removeAttribute('hidden');

    try {
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Failed to fetch next page: ${response.status}`);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      doc.querySelectorAll('#product-grid .grid__item').forEach((item) => {
        this.grid.appendChild(item);
      });

      const freshPagination = doc.querySelector('infinite-scroll-pagination');
      this.nextUrl = freshPagination?.dataset.nextUrl || null;

      history.replaceState({}, '', `${url.pathname}?page=${url.searchParams.get('page') || ''}`);

      if (!this.nextUrl) {
        this.observer?.disconnect();
      }
    } catch (error) {
      console.error(error);
      this.observer?.disconnect();
    } finally {
      this.spinner?.setAttribute('hidden', '');
      this.loading = false;
    }
  }
}

customElements.define('infinite-scroll-pagination', InfiniteScrollPagination);
